// index.ts — public API for the rune-icon bundle.
//
// esbuild bundles everything reachable from here into
// includes/js/dist/rune-icon-bundle.js (IIFE). Exposes window.RuneIcon so any live
// surface (ability bar / character sheet / item page) can render a rune SVG from a
// live `{items, item_layer_types, computed}` ability array — same shape the
// window.FateTide caches produce. See _ai_code_files/runic-icon-production-wiring-handoff.md.
//
// The admin lab (admin_tools/rune_icon_preview.php) also consumes this bundle so the
// lab and production never drift (single source of truth for the renderer).

import {
    compile,
    renderSigil,
    setRuneCatalog,
    compileStatus,
    dmg,
    durLabel,
    typeAccent,
    typeAccent2,
    TYPE_LABEL,
    CAST_LABEL,
    typeBadge,
    AP_BADGE_VARIANTS,
    AP_BADGE_DEFAULT,
} from './rune-icon-engine';
import { renderDetail, wireHover } from './rune-detail';
import type { AbilityArr, RuneOpts } from './types';
import { runeRowsFromFacts } from '../janus-engine/authoring/definition-facts-20';

/** Render an ability array to a rune <svg>. size defaults to 96; sats/cdn/gen default
 *  on; lod omitted → derived from size by renderSigil (>=96→2, >=72→1, <=64→0). */
function render(arr: AbilityArr, opts: RuneOpts = {}): SVGElement {
    // Stamp this arr's catalog (when it carries one) so name1() can resolve bare-UUID reference layers
    // (charge/contest skills etc.) to their item_name — the icon files are name-keyed, so an unresolved
    // UUID 404s. Only set when present so a catalog-less arr doesn't wipe a globally-set caster catalog.
    if (arr.catalog) setRuneCatalog(arr.catalog);
    const o = {
        sats: opts.sats !== false,
        cdn: opts.cdn !== false,
        gen: opts.gen !== false,
        lod: opts.lod ?? '',
        apBadge: opts.apBadge ?? '',
    };
    const size = opts.size ?? 96;
    return renderSigil(compile(arr), size, o) as SVGElement;
}

/** Render + replace the target element's children (for action-bar buttons etc.). */
function mount(target: Element, arr: AbilityArr, opts: RuneOpts = {}): void {
    target.replaceChildren(render(arr, opts));
}

const RuneIcon = {
    prepareRows: runeRowsFromFacts,
    render,
    mount,
    compile,
    // low-level: render an already-compiled `ir` (the admin lab compiles once, renders
    // at several sizes). renderSigil(ir, size, opt) where opt = {sats,cdn,gen,lod}.
    renderSigil,
    // setCatalog(uuidMap) → stamp the ability arr's `catalog` (UUID→{item_name,item_type}) so name1()
    // resolves bare-UUID reference layers to names before compile(). The footer scanner (which calls
    // compile()+renderSigil() directly, bypassing render()) MUST call this first for each mount.
    setCatalog: setRuneCatalog,
    // glow / eyebrow (window.ftMountRuneIcons stamps it as --ft-rune-accent on the card).
    // typeAccent2(ir) → secondary band stop (sphere colour for spells, else == primary),
    // stamped as --ft-rune-accent-2.
    typeAccent,
    typeAccent2,
    // typeBadge(itemType, size) → a tiny shape+colour+AP chip for the ability-TYPE indicator
    // (replaces the flat full_ability.png / quick_ability.png … chips). The icon bundle's
    // ftMountIcons() calls this for ability-type data-icon keys so the chip rhymes with the sigil.
    typeBadge,
    // helpers the preview chrome's legend/readout reuses (kept on the namespace so the
    // lab needs no private copies):
    compileStatus,
    dmg,
    durLabel,
    TYPE_LABEL,
    CAST_LABEL,
    // AP_BADGE_VARIANTS = [{k,label}] — the placement variants the admin lab offers for the
    // explicit action-cost badge (opt.apBadge); AP_BADGE_DEFAULT = the one that actually ships
    // (variant C — spell / menta / curse, the three types whose frame shape doesn't state AP
    // unambiguously). Pass apBadge:'off' to suppress it — that's how the lab A/Bs it.
    AP_BADGE_VARIANTS,
    AP_BADGE_DEFAULT,
    // big-preview chrome (rune-detail.ts) — the admin lab AND the side info panel both
    // render via these so the lab/production preview never drift. renderDetail(host, ir,
    // {legend, sizes, sats, cdn, gen, lod}); wireHover(svg, infoEl) for the hover readout.
    renderDetail,
    wireHover,
};

declare global {
    interface Window {
        RuneIcon: typeof RuneIcon;
        ftMountIcons?: (root?: Element | Document) => void;
    }
}

Object.assign(window, { RuneIcon });

// Upgrade any ability-type icon placeholders the icon bundle deferred while waiting for THIS bundle.
// The icon bundle loads earlier in the footer, so its first mount pass can run before RuneIcon exists;
// it leaves those <i> placeholders un-mounted, and this call (now that typeBadge is available) renders
// them as rune badges. Safe no-op if the icon bundle isn't present or nothing was deferred.
try { window.ftMountIcons?.(); } catch (e) { /* icon bundle absent or not yet ready */ }

export { RuneIcon };
